import { useState } from "react";
import { useTranslation } from "react-i18next";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d7ihj4ehdlg6dog9deu0";

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const subjectOptions = [
    t("contactPage.form.subjectOptions.general"),
    t("contactPage.form.subjectOptions.donation"),
    t("contactPage.form.subjectOptions.volunteer"),
    t("contactPage.form.subjectOptions.partnership"),
    t("contactPage.form.subjectOptions.media"),
    t("contactPage.form.subjectOptions.tech"),
    t("contactPage.form.subjectOptions.other"),
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError(t("contactPage.form.charLimit"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      });
      await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError(t("contactPage.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="py-14 bg-[#1B2838]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("contactPage.tag")}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("contactPage.title")}
          </h1>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            {t("contactPage.subtitle")}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-bold text-[#1B2838] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("contactPage.form.title")}
              </h2>
              {submitted ? (
                <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#4ECDC4]/20 text-[#4ECDC4] mx-auto mb-4">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1B2838] mb-2">{t("contactPage.form.submitted")}</h3>
                  <p className="text-gray-500 text-sm mb-6">{t("contactPage.form.submittedDesc")}</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
                    className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full font-semibold text-sm cursor-pointer"
                  >
                    {t("contactPage.form.another")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-white rounded-xl border border-gray-100 p-8 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("contactPage.form.name")}</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#4ECDC4] transition-colors"
                        placeholder={t("contactPage.form.namePlaceholder")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("contactPage.form.email")}</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#4ECDC4] transition-colors"
                        placeholder={t("contactPage.form.emailPlaceholder")}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("contactPage.form.phone")}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#4ECDC4] transition-colors"
                        placeholder={t("contactPage.form.phonePlaceholder")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("contactPage.form.subject")}</label>
                      <select
                        name="subject"
                        required
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#4ECDC4] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">{t("contactPage.form.subjectPlaceholder")}</option>
                        {subjectOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t("contactPage.form.message")}</label>
                    <textarea
                      name="message"
                      required
                      value={form.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) setForm({ ...form, message: e.target.value });
                      }}
                      rows={5}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#4ECDC4] transition-colors resize-none"
                      placeholder={t("contactPage.form.messagePlaceholder")}
                    />
                    <p className="text-xs text-gray-400 text-right mt-1">{form.message.length}/500</p>
                  </div>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-[#4ECDC4] text-[#1B2838] rounded-full font-bold text-sm cursor-pointer hover:bg-[#3dbdb5] transition-colors disabled:opacity-60"
                  >
                    {submitting ? t("contactPage.form.sending") : t("contactPage.form.submit")}
                  </button>
                  <p className="text-gray-400 text-xs text-center">
                    {t("contactPage.form.privacy")}
                  </p>
                </form>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Office */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-bold text-[#1B2838] text-lg mb-4">{t("contactPage.office.title")}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4ECDC4]/10 text-[#4ECDC4] flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <div className="font-medium text-[#1B2838]">{t("contactPage.office.address")}</div>
                      <div className="text-gray-500">{t("contactPage.office.addressValue")}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4ECDC4]/10 text-[#4ECDC4] flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <div className="font-medium text-[#1B2838]">{t("contactPage.office.hours")}</div>
                      <div className="text-gray-500">{t("contactPage.office.hoursValue")}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4ECDC4]/10 text-[#4ECDC4] flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <div className="font-medium text-[#1B2838]">{t("contactPage.office.phone")}</div>
                      <a href="tel:+16135550192" className="text-gray-500 hover:text-[#4ECDC4] cursor-pointer">+1 (613) 555-0192</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4ECDC4]/10 text-[#4ECDC4] flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <div className="font-medium text-[#1B2838]">{t("contactPage.office.email")}</div>
                      <a href="mailto:info@caf-canada.org" className="text-gray-500 hover:text-[#4ECDC4] cursor-pointer">info@caf-canada.org</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2800.5!2d-75.6972!3d45.4215!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cce05b25f5113af%3A0x8a6a51e131dd15ed!2sOttawa%2C%20ON!5e0!3m2!1sen!2sca!4v1"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="CAF Office Location"
                ></iframe>
                <div className="p-3">
                  <a
                    href="https://maps.google.com/?q=Ottawa+Ontario+Canada"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-[#4ECDC4] text-sm font-medium hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-external-link-line"></i>
                    {t("contactPage.map.directions")}
                  </a>
                </div>
              </div>

              {/* Language Support */}
              <div className="bg-[#1B2838] rounded-xl p-5 text-white">
                <h4 className="font-bold text-sm mb-3">
                  <i className="ri-global-line text-[#4ECDC4] mr-2"></i>
                  {t("contactPage.lang.title")}
                </h4>
                <p className="text-gray-400 text-xs leading-relaxed mb-3">
                  {t("contactPage.lang.desc")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {["English", "پښتو (Pashto)", "دری (Dari)", "Français"].map((lang) => (
                    <span key={lang} className="text-xs bg-white/10 text-gray-300 px-2.5 py-1 rounded-full">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}